import { SignupFlowSlug } from "../constants";
import { useSignupFlowStore } from "../hooks/useSignUpFlowStore";

import type { PrefilledFieldsStoreState } from "./validatePrefilledFields";
import { validatePrefilledFields } from "./validatePrefilledFields";
import { findFirstIncompleteSignupPage } from "./findFirstIncompleteSignupPage";

const START_SLUG = SignupFlowSlug.CAT_COUNT;

export interface RehydrationStoreState extends PrefilledFieldsStoreState {
  versionMismatchDetected: boolean;
}

interface RehydrationRedirect {
  slug: SignupFlowSlug | null;
  reason: "version_mismatch" | "incomplete" | "none";
}

// User recognised as having reached recipes previously if they have an email stored.
function hasReachedRecipes(state: PrefilledFieldsStoreState): boolean {
  return Boolean(state.user?.email);
}

/**
 * Determines where a returning user should be redirected after store
 * rehydration (from localStorage or an AC API response).
 *
 * Checks are performed in priority order:
 * 1. Version mismatch → redirect to the start of the flow.
 * 2. If the user previously reached recipes (email present), find the
 *    first critical incomplete page before RECIPES.
 * 3. Otherwise find the first incomplete page (any) before RECIPES.
 * 4. If the user reached recipes and nothing is incomplete, return to RECIPES.
 * 5. No redirect needed.
 *
 * Shared between the localStorage rehydration guard and the AC restore flow
 * so both entry paths use identical decision logic.
 */
export function getRehydrationRedirect(
  storeState?: RehydrationStoreState,
): RehydrationRedirect {
  const state = storeState ?? useSignupFlowStore.getState();

  if (state.versionMismatchDetected) {
    return { slug: START_SLUG, reason: "version_mismatch" };
  }

  // Validate prefilled fields so validatedFields reflects hydrated data.
  validatePrefilledFields(state, SignupFlowSlug.RECIPES);

  const reachedRecipes = hasReachedRecipes(state);

  const firstIncomplete = findFirstIncompleteSignupPage(
    SignupFlowSlug.RECIPES,
    reachedRecipes ? { criticalOnly: true } : {},
  );

  if (firstIncomplete) {
    return { slug: firstIncomplete, reason: "incomplete" };
  }

  if (reachedRecipes) {
    return { slug: SignupFlowSlug.RECIPES, reason: "incomplete" };
  }

  return { slug: null, reason: "none" };
}
